// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useSyncedInactivityTimer } from "@keller/App/Hooks/useSyncedInactivityTimer";
import { getSettingsCollection } from "@keller/App/Store/Selectors/SettingsSelectors";
import { XNGAGE_CUSTOM } from "@keller/XDL/constants";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    isPunchOutSession: getIsPunchOutSession(state),
    siteTimeoutMinutes: getSettingsCollection(state).customSettings.siteTimeoutMinutes || 15,
});

const mapDispatchToProps = {
    cancelPunchOut,
    signOut,
    loadAccount,
    loadSession,
};

export interface InactiveModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    introText?: TypographyPresentationProps;
    confirmButton?: ButtonPresentationProps;
}

export const inactiveModalStyles: InactiveModalStyles = {};

const styles = inactiveModalStyles;

const InactiveModal = ({
    isPunchOutSession,
    siteTimeoutMinutes,
    cancelPunchOut,
    signOut,
    loadAccount,
    loadSession,
}: Props) => {
    const sessionTimeoutFromBE = siteTimeoutMinutes * 60 * 1000;
    const warningBefore = 1 * 60 * 1000;
    const sessionIntervalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { isWarningModalVisible, resumeFromModal } = useSyncedInactivityTimer({
        sessionTimeout: sessionTimeoutFromBE,
        modalWarningTime: warningBefore,
        onTimeout: () => {
            isPunchOutSession ? cancelPunchOut() : signOut();
        },
    });
    useEffect(() => {
        sessionIntervalTimeoutRef.current = setInterval(() => {
            // loadAccount is used to check if session is expired and sign the user out.
            if (!isWarningModalVisible) {
                loadAccount({ accountId: API_URL_CURRENT_FRAGMENT });
            }
        }, sessionTimeoutFromBE);
        () => sessionIntervalTimeoutRef.current && clearInterval(sessionIntervalTimeoutRef.current);
    }, []);

    const handleResumeActivity = () => {
        resumeFromModal();
        loadSession({ isSessionUpdate: true });
    };

    return (
        <Modal
            {...styles.modal}
            isOpen={isWarningModalVisible}
            handleClose={handleResumeActivity}
            headline={siteMessage("Keller_InactiveModal_HeaderText")}
        >
            <Typography {...styles.introText}>{siteMessage("Keller_InactiveModal_IntroText")}</Typography>
            <Button
                {...styles.confirmButton}
                data-test-selector="inactiveModal_ConfirmButton"
                onClick={handleResumeActivity}
            >
                {siteMessage("Keller_InactiveModal_ConfirmButton")}
            </Button>
        </Modal>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InactiveModal),
    definition: {
        displayName: "Inactive Modal",
        group: XNGAGE_CUSTOM,
    },
};

export default widgetModule;
